import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type ProgressBarVariant = 'full' | 'bar-only'
export type ProgressBarTone = 'info' | 'success' | 'warning' | 'danger'

type ProgressBarTokens = {
  label: string
  muted: string
  track: string
  info: string
  success: string
  warning: string
  danger: string
}

const tokens: Record<ColorScheme, ProgressBarTokens> = {
  dark: {
    label: 'text.secondary',
    // Percent reads as row content alongside the fraction label, so it takes
    // text/secondary like the label rather than the dimmer tertiary.
    muted: 'text.secondary',
    track: 'bg.input',
    info: 'status.info',
    success: 'status.success',
    warning: 'status.warning',
    danger: 'status.error',
  },
  light: {
    label: 'light.text.secondary',
    muted: 'light.text.tertiary',
    track: 'light.bg.input',
    info: 'light.status.info',
    success: 'light.status.success',
    warning: 'light.status.warning',
    danger: 'light.status.error',
  },
}

export const clampProgressValue = (value: number): number =>
  Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))

export const progressBarRootStyles = (
  variant: ProgressBarVariant
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: variant === 'full' ? '6px' : '0',
  w: 'full',
})

export const progressBarHeaderStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  gap: '12px',
  justifyContent: 'space-between',
  w: 'full',
}

export const progressBarLabelStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].label,
  m: 0,
  textStyle: 'caption',
})

export const progressBarFractionStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].label,
  flexShrink: 0,
  m: 0,
  textStyle: 'caption',
})

export const progressBarBodyStyles: SystemStyleObject = {
  alignItems: 'center',
  display: 'flex',
  gap: '8px',
  w: 'full',
}

export const progressBarTrackStyles = (
  colorScheme: ColorScheme,
  variant: ProgressBarVariant
): SystemStyleObject => ({
  bg: tokens[colorScheme].track,
  borderRadius: '999px',
  flex: 1,
  h: variant === 'full' ? '8px' : '6px',
  minW: 0,
  overflow: 'hidden',
})

export const progressBarFillStyles = (
  colorScheme: ColorScheme,
  tone: ProgressBarTone,
  value: number
): SystemStyleObject => ({
  bg: tokens[colorScheme][tone],
  h: 'full',
  transition: 'width 180ms ease',
  w: `${clampProgressValue(value)}%`,
})

export const progressBarPercentStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: tokens[colorScheme].muted,
  flexShrink: 0,
  m: 0,
  minW: '34px',
  textAlign: 'right',
  textStyle: 'caption',
})
